// React
import { useContext, useMemo, useState } from "react"

// Translations
import { useTrans, Trans } from "@/i18n"

// Queries
import {
	UserProfileEmailMutation,
	usePersonalDetailsQuery,
	useUserProfileEmailMutation,
} from "@/api/graphql"
import { useApiV1ProfilePhonePartialUpdate } from "@/api/rest/generated/api/api"
import {
	ApiV1ProfilePhonePartialUpdate200,
	ApiV1ProfilePhonePartialUpdate400,
} from "@/api/rest/generated/@types"

// Forms
import { handleErrorWithFormik } from "@/lib/formik"
import { FormikProvider, useFormik } from "formik"
import {
	FormikInput,
	FormikLabel,
	FormGroup,
	FormikError,
	FormikSubmitButton,
} from "@/components/form-controls"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { useToasts } from "@/context/toasts"
import { Button } from "@/components/Button"

// Icons
import { FiChevronRight, FiX } from "@/lib/icons"

// Context
import {
	ControlStatusEnum,
	useHiddenGetParamsContext,
} from "@/context/hiddenGetParams"
import { RedirectContext } from "@/context/redirectContext"

// Forms
import { Yup, isValidPhoneNumber } from "@/lib/forms"

// Sentry
import { captureMessage } from "@/lib/sentry"

// Security
import { rewriteMangoPayReturnUrlToCurrentUrl } from "@/lib/security"

/**
 * ContactDetailsForm
 * @returns
 */
export function ContactDetailsForm() {
	const t = useTrans(["profile", "investor"])

	// State
	const [showEmailChangeFields, setShowEmailChangeFields] = useState(false)

	// Queries
	const { data } = usePersonalDetailsQuery()

	// Context
	const toasts = useToasts()
	const { controlStatus } = useHiddenGetParamsContext()

	// When control status is validated, show a success toast
	useMemo(() => {
		if (controlStatus === ControlStatusEnum.VALIDATED) {
			toasts.addToast({
				variant: "success",
				id: `personal-details-alert-${Date.now()}`,
				text: t("profile:profile.personal_details.success_message"),
			})
		}

		if (controlStatus === ControlStatusEnum.FAILED) {
			toasts.addToast({
				variant: "alert",
				id: `personal-details-alert-${Date.now()}`,
				text: t("profile:profile.personal_details.failed_message"),
			})
		}
	}, [controlStatus])

	// Template
	return (
		<CardWrapper>
			<CardBody>
				<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
					{t("profile:profile.contact_details.title")}
				</Heading>
				<FormGroup
					heading={
						<>
							<p className="whitespace-break-spaces">
								{t("profile:profile.contact_details.copy")}
							</p>

							{/** Logo */}
							<img
								src="/assets/partners/mangopay_logo.png"
								alt="Mangopay logo"
								className="w-1/2"
							/>

							{/** FAQ */}
							<p className="whitespace-break-spaces">
								<Trans
									i18nKey="investor:investor.identity.copy.mangopay.copy.faq"
									components={{
										/* eslint-disable jsx-a11y/anchor-has-content */
										a: (
											<a
												className="font-medium text-secondary-300 hover:text-secondary-700"
												href="https://informatie.zonhub.com/support/solutions/articles/17000147317-hoe-mangopay-als-payment-service-provider-zonhub-helpt"
												target="_blank"
												rel="noreferrer"
											/>
											/* eslint-enable jsx-a11y/anchor-has-content */
										),
									}}
								/>
							</p>
						</>
					}
				>
					<div
						className="w-fulls grid gap-6 2xl:grid-cols-2"
						data-testid="contact-details-form-group"
					>
						{/**  Email */}
						<EmailForm
							initialEmailAddress={data?.me?.email}
							showEmailChangeFields={showEmailChangeFields}
							setShowEmailChangeFields={setShowEmailChangeFields}
						/>

						{/**  Phone number */}
						{showEmailChangeFields === false ? (
							<PhoneNumberForm
								initialPhoneNumber={data?.me?.profile?.phone}
							/>
						) : null}
					</div>
				</FormGroup>
			</CardBody>
		</CardWrapper>
	)
}

/**
 * EmailForm
 * @returns
 */
function EmailForm({
	initialEmailAddress,
	showEmailChangeFields,
	setShowEmailChangeFields,
}: {
	initialEmailAddress: string | null | undefined
	showEmailChangeFields: boolean
	setShowEmailChangeFields: React.Dispatch<React.SetStateAction<boolean>>
}) {
	// Translate
	const t = useTrans(["profile", "investor"])

	// Context
	const toasts = useToasts()
	const { redirect } = useContext(RedirectContext)

	// Forms
	const updateEmail = useUserProfileEmailMutation()
	const formEmail = useFormik({
		enableReinitialize: true,
		validationSchema: Yup.object({
			email: Yup.string()
				.email(
					t("profile:profile.personal_details.email.error_invalid"),
				)
				.required(
					t("profile:profile.personal_details.email.error_required"),
				),
			confirm_email: Yup.string()
				.email(
					t("profile:profile.personal_details.email.error_invalid"),
				)
				.required(
					t("profile:profile.personal_details.email.error_required"),
				)
				.oneOf(
					[Yup.ref("email")],
					t("profile:profile.personal_details.email.error_mismatch"),
				),
			current_password: Yup.string().required(
				t("profile:profile.personal_details.password.error_required"),
			),
		}),
		initialValues: {
			email: initialEmailAddress ?? "",
			confirm_email: "",
			current_password: "",
		},
		onSubmit: async () => {
			await updateEmail
				.mutateAsync({
					new_email: formEmail.values.email,
					confirm_email: formEmail.values.confirm_email,
					current_password: formEmail.values.current_password,
				})
				.then((response: UserProfileEmailMutation) => {
					// Success email change
					if (response?.email_update?.new_email) {
						toasts.addToast({
							variant: "success",
							id: `change-email-success-${Date.now()}`,
							text: `Changed email success`,
						})
					}

					// Redirect If we have verification url
					if (response.email_update?.verification_url) {
						redirect(
							rewriteMangoPayReturnUrlToCurrentUrl(
								response.email_update?.verification_url,
							),
						)
					}

					// otherwise show a message that there are form errors
					if (response?.email_update?.errors) {
						toasts.addToast({
							variant: "alert",
							id: `change-email-failed-${Date.now()}`,
							text: `Failed changing email: ${response?.email_update?.errors?.at(0)?.messages?.[0]}`,
						})
					}
				})
				.catch((response) => {
					if (response.error) {
						toasts.addToast({
							variant: "alert",
							id: `bank-details-alert-${Date.now()}`,
							text: "Failed to update contact details",
						})

						handleErrorWithFormik(
							new Error(response.error),
							formEmail,
						)
					}
				})
		},
	})

	// Template
	return (
		<form
			onSubmit={formEmail.handleSubmit}
			className="lg:w-1/2 2xl:col-span-2"
		>
			<FormikProvider value={formEmail}>
				<div className="space-y-4">
					<div className="flex flex-row gap-2">
						<div className="flex-1">
							<FormikLabel htmlFor="email" className="flex">
								{t(
									"profile:profile.personal_details.email.title",
								)}
							</FormikLabel>
							<FormikInput
								name="email"
								required
								className="mt-1 block w-full"
							/>
						</div>

						{/* Show verify button to 'continue' change email flow */}
						{!showEmailChangeFields ? (
							<Button
								type="button"
								className="self-end"
								disabled={!formEmail.dirty}
								onClick={() => setShowEmailChangeFields(true)}
							>
								{t("profile.contact_details.confirm")}
							</Button>
						) : null}
					</div>

					{/** When changing email */}
					{showEmailChangeFields && (
						<>
							<div className="flex flex-row gap-2">
								<div className="flex-1">
									<FormikLabel
										htmlFor="confirm_email"
										className="flex"
									>
										{t(
											"profile:profile.personal_details.email.confirm",
										)}
									</FormikLabel>
									<FormikInput
										name="confirm_email"
										type="email"
										required
										className="mt-1 block w-full"
									/>
									<FormikError field="confirm_email" />
								</div>
							</div>

							<div className="flex flex-row gap-2">
								<div className="flex-1">
									<FormikLabel
										htmlFor="current_password"
										className="flex"
									>
										{t(
											"profile:profile.personal_details.password.current",
										)}
									</FormikLabel>
									<FormikInput
										name="current_password"
										type="password"
										required
										className="mt-1 block w-full"
									/>
									<FormikError field="current_password" />
								</div>
							</div>

							<div className="flex justify-end">
								{/** Cancel */}
								<Button
									type="button"
									variant="transparent"
									className="mr-4 self-end"
									onClick={() => {
										setShowEmailChangeFields(false)
										formEmail.resetForm()
									}}
								>
									<FiX className="mr-2" />
									{t("profile.contact_details.cancel")}
								</Button>

								{/** Submit */}
								<FormikSubmitButton
									className="self-end"
									disableWhenPristine
								>
									{t(
										"profile:profile.contact_details.confirm",
									)}
									<FiChevronRight className="ml-2" />
								</FormikSubmitButton>
							</div>
						</>
					)}

					<FormikError field="email" />
				</div>
			</FormikProvider>
		</form>
	)
}

/**
 * PhoneNumberForm
 * @returns
 */
function PhoneNumberForm({
	initialPhoneNumber = "",
}: {
	initialPhoneNumber: string | null | undefined
}) {
	// Translate
	const t = useTrans(["profile", "investor"])

	// Context
	const toasts = useToasts()
	const { redirect } = useContext(RedirectContext)

	// Form
	const updatePhoneNumber = useApiV1ProfilePhonePartialUpdate({
		mutation: {
			onSuccess: async (response: ApiV1ProfilePhonePartialUpdate200) => {
				// do we have data? then success
				if (response?.verification_url) {
					redirect(
						rewriteMangoPayReturnUrlToCurrentUrl(
							response.verification_url,
						),
					)
				}
				// If no verification url, show a success toast
				else {
					toasts.addToast({
						variant: "success",
						id: `personal-details-alert-${Date.now()}`,
						text: t(
							"profile:profile.personal_details.success_message",
						),
					})
				}
			},
			onError: (response: ApiV1ProfilePhonePartialUpdate400) => {
				if (response) {
					toasts.addToast({
						variant: "alert",
						id: `bank-details-alert-${Date.now()}`,
						text: response.phone[0],
					})
					// Log errors to sentry
					captureMessage("Saving phone number failed", {
						level: "error",
						extra: {
							response,
						},
					})
				}
			},
		},
	})
	const formPhone = useFormik({
		enableReinitialize: true,
		validationSchema: Yup.object({
			phone: Yup.string()
				.optional()
				.nullable()
				.test("valid-phone-number", "Invalid phone number", (value) => {
					return isValidPhoneNumber(value ?? "")
				}),
		}),
		initialValues: {
			phone: initialPhoneNumber ?? undefined,
		},
		onSubmit: async () => {
			await updatePhoneNumber.mutateAsync({
				data: {
					phone: formPhone.values.phone,
					// TODO: returnUrl: "",
				},
			})
		},
	})

	// Template
	return (
		<form
			onSubmit={formPhone.handleSubmit}
			className="lg:w-1/2 2xl:col-span-2"
		>
			<FormikProvider value={formPhone}>
				<div className="flex flex-row gap-2">
					<div className="flex-1">
						<FormikLabel htmlFor="phone" className="flex">
							<span className="flex items-center">
								{t(
									"profile:profile.personal_details.phone_number.title",
								)}
							</span>
						</FormikLabel>
						<FormikInput
							type="phone"
							name="phone"
							onChange={(value) => {
								void formPhone.setFieldValue("phone", value)
							}}
							required
							className="mt-1 block w-full"
						/>
					</div>

					<FormikSubmitButton
						className="self-end"
						disableWhenPristine
					>
						{t("profile:profile.contact_details.confirm")}
						<FiChevronRight className="ml-2" />
					</FormikSubmitButton>
				</div>
				<FormikError field="phone" />
			</FormikProvider>
		</form>
	)
}
